import type { EventItem } from "@/services/content";

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatDateOnlyToICS(dateStr: string): string {
  // dateStr expected in YYYY-MM-DD
  return dateStr.replaceAll("-", "");
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  return `${yyyy}${mm}${dd}`;
}

function formatNowUTCAsICS(): string {
  const d = new Date();
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

function escapeText(text: string | undefined | null): string {
  if (!text) return "";
  return text
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

export function buildEventICS(event: EventItem): string {
  const dtstamp = formatNowUTCAsICS();
  const dtstart = formatDateOnlyToICS(event.date);
  const dtend = addDays(event.date, 1); // all-day events end next day (exclusive)
  const uid = `${event.id}@ifiheritage.center`;

  const descriptionParts = [event.description];
  if (event.time) descriptionParts.push(`Time: ${event.time}`);
  if (event.location) descriptionParts.push(`Location: ${event.location}`);
  const description = escapeText(descriptionParts.filter(Boolean).join("\n"));

  const lines = [
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;VALUE=DATE:${dtstart}`,
    `DTEND;VALUE=DATE:${dtend}`,
    `SUMMARY:${escapeText(event.title)}`,
    event.location ? `LOCATION:${escapeText(event.location)}` : undefined,
    description ? `DESCRIPTION:${description}` : undefined,
    "END:VEVENT",
  ].filter(Boolean) as string[];

  return lines.join("\r\n");
}

export function buildEventsICS(events: EventItem[]): string {
  const vevents = events.map(buildEventICS).join("\r\n");
  const header = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//My Organization//Events//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "X-WR-CALNAME:Organization Events",
  ].join("\r\n");
  const footer = "END:VCALENDAR";
  return [header, vevents, footer].join("\r\n");
}

export function downloadICSFile(filename: string, icsContent: string) {
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
